<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import { accessoryGet } from "@/common/api/rest/accessory/accessory";

import type { AccessoryType } from "@/common/types/accessory/accessory";

import TabAccessoryInfos from "@/components/tabs/accessory/infos.vue";
import TabAccessoryDetails from "@/components/tabs/accessory/details.vue";
import TabAccessorySlug from "@/components/tabs/accessory/slug.vue";
import TabAccessoryImages from "@/components/tabs/accessory/images.vue";
import TabAccessoryName from "@/components/tabs/accessory/name.vue";
import TabAccessoryConsole from "@/components/tabs/accessory/console.vue";

import { breadcrumbSimple, iconMenu, svgIcon } from "@/common/components";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const selectedTab = ref("infos");
const accessoryData = ref<AccessoryType | null>(null);

const accessoryId = computed(() => route.params.item as string);

const breadcrumbList = computed(() => [
  {
    key: "collections",
    label: t("common.labels.collections"),
    url: `/collection`,
  },
  {
    key: "accessory",
    label: t("collection.labels.accessories"),
    url: `/collection/accessory`,
  },
  {
    key: "edit",
    label: accessoryData.value?.name ?? "...",
  },
]);

const menuList = computed(() => [
  {
    key: "infos",
    icon: "field",
    label: t("collection.labels.infos"),
    url: `/collection/accessory/${accessoryId.value}/infos`,
  },
  {
    key: "name",
    icon: "no-image",
    label: t("common.labels.other_names"),
    url: `/collection/accessory/${accessoryId.value}/name`,
  },
  {
    key: "slug",
    icon: "no-image",
    label: t("slug.labels.slugs"),
    url: `/collection/accessory/${accessoryId.value}/slug`,
  },
  {
    key: "image",
    icon: "no-image",
    label: t("collection.labels.images"),
    url: `/collection/accessory/${accessoryId.value}/image`,
  },
  {
    key: "details",
    icon: "no-image",
    label: t("collection.labels.details"),
    url: `/collection/accessory/${accessoryId.value}/details`,
  },
  {
    key: "console",
    icon: "no-image",
    label: t("collection.labels.consoles"),
    url: `/collection/accessory/${accessoryId.value}/console`,
  },
]);

function back() {
  router.push(`/collection/accessory`);
}

async function changeMenu(url: string) {
  await fetchData();
  router.push(url);
}

// accessory
async function fetchData() {
  await accessoryGet(accessoryId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        accessoryData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => route.params.tab,
  () => {
    selectedTab.value = route.params.tab as string;
  },
  {
    immediate: true,
  },
);

onMounted(async () => {
  await fetchData();
});
</script>

<template>
  <article class="register-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        url-home="/home"
        active="edit"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <div class="title">
          <svg-icon icon="gamepad" size="2rem" />
          EDITAR ACESSÓRIO
        </div>

        <div class="actions">
          <button class="btn btn-default w-8 h-8" @click="back()">
            <svg-icon icon="close" size="1rem" />
          </button>
        </div>
      </div>

      <div class="tab-content">
        <icon-menu
          :tabs="menuList"
          :active="selectedTab"
          size="3rem"
          @selected="changeMenu($event)"
        />

        <template v-if="accessoryData">
          <template v-if="selectedTab === 'infos'">
            <tab-accessory-infos
              :accessory-id="accessoryId"
              :accessory="accessoryData"
              @update:data="fetchData()"
            />
          </template>

          <template v-if="selectedTab === 'slug'">
            <tab-accessory-slug :accessory-id="accessoryId" />
          </template>

          <template v-if="selectedTab === 'details'">
            <tab-accessory-details :accessory-id="accessoryId" />
          </template>

          <template v-if="selectedTab === 'name'">
            <tab-accessory-name :owner-id="accessoryId" />
          </template>

          <template v-if="selectedTab === 'console'">
            <tab-accessory-console :owner-id="accessoryId" />
          </template>

          <template v-if="selectedTab === 'image'">
            <tab-accessory-images :accessory-id="accessoryId" />
          </template>
        </template>
      </div>
    </section>
  </article>
</template>

<style lang="scss" scoped>
.register-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      margin-block-end: 1.5rem;
      border-block-end: 1px solid var(--border);

      .title {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.2rem;
        text-transform: uppercase;
      }

      .actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;

        button {
          width: 1.75rem;
          height: 1.75rem;
          padding: 0;
        }
      }
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      gap: 1.54rem;
      padding-inline: 1.25rem;
      padding-block-end: 1.25rem;
    }
  }
}
</style>
